import { NextPayment } from '../../types';
import { NavRoute } from './types';

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: n % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}`;

const valSkeleton = () => (
  <span
    className='inline-block h-[1.35em] min-w-[5rem] rounded-md bg-white/20 align-middle animate-pulse'
    aria-hidden
  />
);

const StreakIcon = () => (
  <svg
    className='rd-stat-icon'
    viewBox='0 0 24 24'
    fill='none'
    stroke='var(--color-green)'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
  </svg>
);

export const MetricsOverview = ({
  nextPayment,
  pointsBalance,
  streakValue,
  autoPayOn,
  onNav,
  pendingNextPayment,
  pendingPoints,
  pendingStreak,
  isFirstRent,
}: {
  nextPayment: NextPayment;
  pointsBalance: number;
  streakValue: number;
  streakPct?: number;
  autoPayOn: boolean;
  onNav: (route: NavRoute) => void;
  pendingNextPayment: boolean;
  pendingPoints: boolean;
  pendingStreak: boolean;
  isFirstRent: boolean;
}) => {
  const activeDots = Math.min(6, streakValue);
  const payRentLabel = isFirstRent ? 'Pay your rent' : 'Pay your rent';

  return (
    <div className='rd-stats'>
      <div className='rd-stat rd-stat-featured'>
        <div className='rd-stat-particle-layer' aria-hidden='true'>
          <span className='rd-stat-particle p1' />
          <span className='rd-stat-particle p2' />
          <span className='rd-stat-particle p3' />
        </div>
        <div className='rd-stat-lbl'>Next payment</div>
        <div className='rd-stat-val-row'>
          <div className='rd-stat-val'>
            {pendingNextPayment ? valSkeleton() : money(nextPayment.amount)}
          </div>
        </div>
        <div className='rd-stat-sub'>
          {pendingNextPayment ? (
            <span className='inline-block h-[1em] w-[14rem] max-w-full rounded-md bg-white/15 animate-pulse' />
          ) : (
            <>
              {nextPayment.days === 0 ? 'Rent due today' : `Due in ${nextPayment.days} ${nextPayment.days === 1 ? 'day' : 'days'}`} ·{' '}
              {nextPayment.dueText}
            </>
          )}
        </div>
        <div className='rd-stat-foot rd-stat-foot-pay'>
          <button type='button' className='rd-stat-pay-btn' onClick={() => onNav('pay')}>
            <span aria-hidden='true'>+</span>
            {payRentLabel}
          </button>
          <span className={`rd-stat-hint ${!autoPayOn ? 'rd-stat-hint-warn' : ''}`}>
            {autoPayOn ? 'Auto-pay on' : 'Auto-pay off'}
          </span>
        </div>
      </div>

      <div className='rd-stat rd-stat-featured'>
        <div className='rd-stat-particle-layer' aria-hidden='true'>
          <span className='rd-stat-particle p1' />
          <span className='rd-stat-particle p2' />
          <span className='rd-stat-particle p3' />
        </div>
        <div className='rd-stat-lbl'>Points balance</div>
        <div className='rd-stat-val-row'>
          <div className='rd-stat-val'>
            {pendingPoints ? valSkeleton() : pointsBalance.toLocaleString()}
          </div>
        </div>
        <div className='rd-stat-sub'>Points</div>
        <div className='rd-stat-foot'>
          <button type='button' className='rd-stat-cta' onClick={() => onNav('wallet')}>
            Redeem
          </button>
          <button
            type='button'
            className='rd-stat-hint rd-stat-hint-link'
            onClick={() => onNav('wallet')}
          >
            115+ brands
          </button>
        </div>
      </div>

      <div className='rd-stat rd-stat-green'>
        <div className='rd-stat-lbl'>Payment streak</div>
        <div className='rd-stat-val-row'>
          <StreakIcon />
          <div className='rd-stat-val'>{pendingStreak ? valSkeleton() : streakValue}</div>
        </div>
        <div className='rd-stat-sub'>Months on time</div>
        <div className='rd-stat-foot'>
          <div className='rd-streak-dots' aria-hidden='true'>
            {Array.from({ length: 6 }).map((_, index) => (
              <span key={index} className={index < activeDots ? 'active' : undefined} />
            ))}
          </div>
          <span className='rd-stat-hint'>Pay to build streak</span>
        </div>
      </div>
    </div>
  );
};
